import * as React from "react";
import {
  Box,
  Button,
  IconButton,
  InputBase,
  Menu,
  MenuItem,
  Select,
  type SelectChangeEvent,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CloseIcon from "@mui/icons-material/Close";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { type Dayjs } from "dayjs";
import type { GridColDef } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { messages } from "../../../../locales/messages";
import {
  getDefaultOperator,
  getOperatorMessageId,
  getOperatorsForType,
  normalizeColumnType,
  operatorNeedsValue,
  showOperatorMenu,
  type HeaderFilterColumnType,
} from "./filterOperators";
import {
  normalizeValueOptions,
  resolveFilterLabel,
  type ValueOption,
} from "./filterFormat";

export type HeaderFilterCellProps = {
  field: string;
  type?: GridColDef["type"];
  valueOptions?: readonly ValueOption[];
  value: unknown;
  operator: string;
  /** Column width (px), used to compact the controls in narrow columns. */
  width?: number;
  onChange: (field: string, operator: string, value: unknown) => void;
  onClear: (field: string) => void;
};

const COMPACT_WIDTH = 132;
const FILTER_INPUT_DEBOUNCE_MS = 300;

const stopGridHeaderInteraction = (event: React.SyntheticEvent) => {
  event.stopPropagation();
};

function HeaderFilterCellComponent({
  field,
  type,
  valueOptions,
  value,
  operator,
  width,
  onChange,
  onClear,
}: HeaderFilterCellProps) {
  const { t } = useTranslation();
  const columnType = normalizeColumnType(type);
  const operators = getOperatorsForType(type);
  const currentOperator = operator || getDefaultOperator(type);
  const needsValue = operatorNeedsValue(currentOperator);
  const canShowOperatorMenu = showOperatorMenu(type);
  const compact = typeof width === "number" && width < COMPACT_WIDTH;
  const [operatorAnchor, setOperatorAnchor] = React.useState<HTMLElement | null>(
    null,
  );
  const menuOpen = Boolean(operatorAnchor);

  const isTextInput = columnType === "string" || columnType === "number";
  const toDraft = (v: unknown) =>
    v === undefined || v === null ? "" : String(v);

  // Text/number inputs keep their value in local state so each keystroke re-renders
  // only this cell. The model (and the grid) update on a short debounce instead of on
  // every key — otherwise typing churns the entire DataGrid and feels laggy.
  const [draft, setDraft] = React.useState<string>(toDraft(value));
  const draftRef = React.useRef(draft);
  draftRef.current = draft;
  const draftTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastEmittedRef = React.useRef<string>(toDraft(value));
  const operatorRef = React.useRef(currentOperator);
  operatorRef.current = currentOperator;

  React.useEffect(() => {
    // Sync when the value changes for external reasons (clear, operator reset, an
    // incoming filterModel) — but not from our own just-emitted echo.
    const next = toDraft(value);
    if (next !== lastEmittedRef.current) {
      lastEmittedRef.current = next;
      setDraft(next);
    }
  }, [value]);

  React.useEffect(
    () => () => {
      if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
    },
    [],
  );

  const emitDraft = React.useCallback(
    (text: string) => {
      lastEmittedRef.current = text;
      onChange(field, operatorRef.current, text);
    },
    [field, onChange],
  );

  const cancelDraft = React.useCallback(() => {
    if (draftTimerRef.current) {
      clearTimeout(draftTimerRef.current);
      draftTimerRef.current = null;
    }
  }, []);

  const flushDraft = React.useCallback(() => {
    if (draftTimerRef.current) {
      clearTimeout(draftTimerRef.current);
      draftTimerRef.current = null;
      emitDraft(draftRef.current);
    }
  }, [emitDraft]);

  const handleTextChange = React.useCallback(
    (text: string) => {
      setDraft(text);
      draftRef.current = text;
      if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
      draftTimerRef.current = setTimeout(() => {
        draftTimerRef.current = null;
        emitDraft(text);
      }, FILTER_INPUT_DEBOUNCE_MS);
    },
    [emitDraft],
  );

  const operatorLabel = resolveFilterLabel(
    t,
    getOperatorMessageId(type, currentOperator),
  );
  const operatorAria = `${t(messages.Filter_operator_label())}: ${operatorLabel}`;

  const inputProps = {
    onMouseDown: stopGridHeaderInteraction,
    onClick: stopGridHeaderInteraction,
    onKeyDown: (event: React.KeyboardEvent) => {
      if (
        (event.ctrlKey || event.metaKey) &&
        event.key === "Enter" &&
        canShowOperatorMenu
      ) {
        event.preventDefault();
        setOperatorAnchor(event.currentTarget as HTMLElement);
        return;
      }
      stopGridHeaderInteraction(event);
    },
  };

  const openOperatorMenu = (event: React.MouseEvent<HTMLElement>) => {
    stopGridHeaderInteraction(event);
    // Commit any pending typed value before the operator changes.
    flushDraft();
    setOperatorAnchor(event.currentTarget);
  };

  const handleOperatorSelect = (nextOperator: string) => {
    setOperatorAnchor(null);
    operatorRef.current = nextOperator;
    if (!operatorNeedsValue(nextOperator)) {
      cancelDraft();
      onChange(field, nextOperator, undefined);
      return;
    }
    const currentValue = isTextInput ? draftRef.current : (value ?? "");
    onChange(field, nextOperator, currentValue);
  };

  const hasValue = isTextInput
    ? draft !== ""
    : value !== undefined &&
      value !== null &&
      value !== "" &&
      !(Array.isArray(value) && value.length === 0);

  // A filter is "active" when it constrains rows: either it has a value, or it is a
  // value-less operator (isEmpty/isNotEmpty) which is meaningful on its own.
  const isActive = hasValue || !needsValue;
  const showClear = isActive;

  const renderValueInput = () => {
    if (!needsValue) {
      return (
        <Box className="header-filter-no-value" aria-hidden>
          {t(messages.Filter_no_value())}
        </Box>
      );
    }

    switch (columnType as HeaderFilterColumnType) {
      case "boolean": {
        const selectValue =
          value === true || value === "true"
            ? "true"
            : value === false || value === "false"
              ? "false"
              : "";

        return (
          <Select
            size="small"
            fullWidth
            displayEmpty
            variant="standard"
            disableUnderline
            value={selectValue}
            {...inputProps}
            onChange={(e: SelectChangeEvent) => {
              const v = e.target.value;
              if (v === "") {
                onClear(field);
              } else {
                onChange(field, currentOperator, v === "true");
              }
            }}
            className="header-filter-value-input"
          >
            <MenuItem value="">
              <em>{t(messages.Filter_any())}</em>
            </MenuItem>
            <MenuItem value="true">{t(messages.Filter_bool_true())}</MenuItem>
            <MenuItem value="false">{t(messages.Filter_bool_false())}</MenuItem>
          </Select>
        );
      }

      case "singleSelect": {
        const options = normalizeValueOptions(valueOptions);
        const selectValue =
          value === undefined || value === null ? "" : String(value);

        return (
          <Select
            size="small"
            fullWidth
            displayEmpty
            variant="standard"
            disableUnderline
            value={selectValue}
            {...inputProps}
            onChange={(e: SelectChangeEvent) => {
              const v = e.target.value;
              if (v === "") {
                onClear(field);
                return;
              }
              const matched = options.find((o) => String(o.value) === v);
              onChange(field, currentOperator, matched?.value ?? v);
            }}
            className="header-filter-value-input"
          >
            <MenuItem value="">
              <em>{t(messages.Filter_any())}</em>
            </MenuItem>
            {options.map((opt) => (
              <MenuItem key={String(opt.value)} value={String(opt.value)}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        );
      }

      case "date":
      case "dateTime": {
        const dateValue: Dayjs | null =
          value instanceof Date
            ? dayjs(value)
            : value
              ? dayjs(String(value))
              : null;

        return (
          <DatePicker
            value={dateValue?.isValid() ? dateValue : null}
            slotProps={{
              textField: {
                size: "small",
                fullWidth: true,
                variant: "standard",
                placeholder: t(messages.Filter_value_placeholder()),
                className: "header-filter-value-input",
                onMouseDown: stopGridHeaderInteraction,
                onClick: stopGridHeaderInteraction,
                onKeyDown: inputProps.onKeyDown,
              },
            }}
            onChange={(newDate) => {
              if (!newDate || !newDate.isValid()) {
                onClear(field);
                return;
              }
              onChange(field, currentOperator, newDate.toISOString());
            }}
          />
        );
      }

      case "number":
        return (
          <InputBase
            fullWidth
            type="number"
            placeholder={t(messages.Filter_value_placeholder())}
            value={draft}
            {...inputProps}
            onChange={(e) => handleTextChange(e.target.value)}
            onBlur={flushDraft}
            className="header-filter-value-input"
          />
        );

      default:
        return (
          <InputBase
            fullWidth
            placeholder={t(messages.Filter_search_placeholder())}
            value={draft}
            {...inputProps}
            onChange={(e) => handleTextChange(e.target.value)}
            onBlur={flushDraft}
            className="header-filter-value-input"
          />
        );
    }
  };

  return (
    <Box
      className={`header-filter-cell-root${compact ? " compact" : ""}${isActive ? " is-active" : ""}`}
      // Stop our interactive content's events from reaching MUI's column-header
      // handlers. Their `event.currentTarget.contains(event.target)` guard throws
      // on portal'd targets (Select/Menu/DatePicker) — focus/blur are the discrete
      // events that otherwise slip through and crash GridColumnHeaderItem.
      onMouseDown={stopGridHeaderInteraction}
      onClick={stopGridHeaderInteraction}
      onDoubleClick={stopGridHeaderInteraction}
      onKeyDown={stopGridHeaderInteraction}
      onFocus={stopGridHeaderInteraction}
      onBlur={stopGridHeaderInteraction}
    >
      {canShowOperatorMenu && (
        <>
          <Button
            size="small"
            variant="text"
            disableRipple
            className={`header-filter-operator-chip${isActive ? " is-active" : ""}`}
            endIcon={<ArrowDropDownIcon />}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label={operatorAria}
            title={t(messages.Filter_change_operator())}
            draggable={false}
            onMouseDown={stopGridHeaderInteraction}
            onClick={openOperatorMenu}
          >
            {operatorLabel}
          </Button>
          <Menu
            anchorEl={operatorAnchor}
            open={menuOpen}
            onClose={() => setOperatorAnchor(null)}
            onClick={stopGridHeaderInteraction}
          >
            {operators.map((op) => (
              <MenuItem
                key={op.value}
                selected={op.value === currentOperator}
                onClick={() => handleOperatorSelect(op.value)}
              >
                {resolveFilterLabel(t, op.messageId)}
              </MenuItem>
            ))}
          </Menu>
        </>
      )}

      <Box className="header-filter-value-row">
        <Box className="header-filter-value-wrap">{renderValueInput()}</Box>
        {showClear && (
          <IconButton
            size="small"
            className="header-filter-clear-button"
            aria-label={t(messages.Filter_clear())}
            title={t(messages.Filter_clear())}
            draggable={false}
            onMouseDown={stopGridHeaderInteraction}
            onClick={(event) => {
              stopGridHeaderInteraction(event);
              cancelDraft();
              setDraft("");
              onClear(field);
            }}
          >
            <CloseIcon fontSize="inherit" />
          </IconButton>
        )}
      </Box>
    </Box>
  );
}

export const HeaderFilterCell = React.memo(HeaderFilterCellComponent);
